import Header from "../components/Header";

const LAST_UPDATED = "8 May 2026";
const VERSION = "1.0";

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-2xl font-black text-[#ffd978] mb-3">{title}</h2>
      <div className="space-y-3 text-white/75 leading-relaxed">{children}</div>
    </div>
  );
}

function Sub({ title, children }) {
  return (
    <div className="mt-4">
      <h3 className="font-black text-white/90 mb-2">{title}</h3>
      <div className="space-y-2 text-white/70">{children}</div>
    </div>
  );
}

function List({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export default function StaffAgreement() {
  return (
    <main className="bg-[#050505] text-white">
      <Header />

      <section className="mx-auto max-w-4xl px-6 pt-36 pb-20 select-text">
        <p className="font-black uppercase text-[#ffb56f]">
          Internal — Staff Only
        </p>
        <h1 className="outfall-heading text-[clamp(48px,8vw,90px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
          Staff Agreement
        </h1>
        <p className="mt-4 text-white/40 font-bold">
          Version {VERSION} — {LAST_UPDATED}
        </p>

        <div className="mt-6 rounded-xl border border-[#ffb56f]/30 bg-[#ffb56f]/5 px-5 py-4">
          <p className="font-bold text-[#ffb56f] text-sm">
            By accepting a staff role at Outfall, you agree to be bound by this
            Agreement in full. This document is confidential and must not be
            shared outside of the staff team.
          </p>
        </div>

        <div className="mt-12 space-y-10">
          <Section title="1. Scope">
            <p>
              This Agreement applies to all individuals granted a staff role at
              Outfall, regardless of tier or department, including:
            </p>
            <List
              items={[
                "Moderators",
                "Administrators",
                "Developers",
                "Builders and Designers",
                "Any person granted elevated access to Outfall systems",
              ]}
            />
          </Section>

          <Section title="2. Data Access and Confidentiality">
            <Sub title="2.1 Personal Data">
              <p>
                Through your role you may access personal data belonging to
                applicants and community members, including Discord usernames
                and IDs, email addresses, Roblox usernames, application
                responses, ban appeal contents, and staff decision notes.
              </p>
              <p className="mt-2">You agree to:</p>
              <List
                items={[
                  "Access personal data only when necessary to perform your duties",
                  "Never share, screenshot, forward, or discuss personal data outside of designated staff channels",
                  "Never use applicant or appeal data for personal purposes",
                  "Never share admin panel access or credentials with anyone not authorised by leadership",
                  "Report any accidental data exposure to leadership immediately",
                ]}
              />
            </Sub>
            <Sub title="2.2 Admin Panel">
              <p>
                Access to{" "}
                <code className="bg-white/10 px-1 rounded text-sm">
                  /careers-review
                </code>{" "}
                and{" "}
                <code className="bg-white/10 px-1 rounded text-sm">
                  /appeals-review
                </code>{" "}
                is granted solely for reviewing applications and appeals. You
                must not:
              </p>
              <List
                items={[
                  "View or modify records unrelated to your current duties",
                  "Access, modify, or delete records outside your remit",
                  "Share the admin panel URL or your access with non-staff members",
                ]}
              />
            </Sub>
          </Section>

          <Section title="3. Conduct Standards">
            <Sub title="3.1 In the Discord Server">
              <List
                items={[
                  "Treat all community members with respect at all times",
                  "Do not abuse moderation tools outside of genuine rule violations",
                  "Do not engage in personal disputes using your staff permissions",
                  "Do not use your position to give preferential treatment to friends",
                  "Escalate serious situations to senior staff rather than acting unilaterally",
                ]}
              />
            </Sub>
            <Sub title="3.2 In the Game">
              <List
                items={[
                  "Do not use staff access to cheat, spawn items, or gain in-game advantages",
                  "Do not manipulate game data for yourself or others",
                  "Report exploits or security issues to leadership immediately — do not abuse or publicise them",
                ]}
              />
            </Sub>
            <Sub title="3.3 Representing Outfall">
              <List
                items={[
                  "You represent Outfall whenever you act in your staff capacity",
                  "Do not make public statements about Outfall policy or planned features unless authorised",
                  "Do not impersonate other staff members or claim authority you do not have",
                ]}
              />
            </Sub>
          </Section>

          <Section title="4. Applications and Appeals">
            <Sub title="4.1 Career Applications">
              <List
                items={[
                  "Review applications fairly and without bias",
                  "Do not discuss or share applicant details outside of the staff review process",
                  "Do not accept or reject applications based on personal relationships",
                  "All decisions must be recorded in the admin panel",
                ]}
              />
            </Sub>
            <Sub title="4.2 Ban Appeals">
              <List
                items={[
                  "Review appeals on their merits only — declare prior personal history with an appellant to senior staff",
                  "All decisions must include a clear, professional staff note — it is sent directly to the user",
                  "Do not deny appeals based on personal dislike",
                  "Escalate uncertain decisions to senior staff before acting",
                ]}
              />
            </Sub>
          </Section>

          <Section title="5. Confidentiality of Internal Operations">
            <p>You agree not to disclose:</p>
            <List
              items={[
                "Internal staff discussions, decisions, or debates",
                "Information about other staff members' roles, standing, or performance",
                "Planned features, updates, or changes before official announcement",
                "Any security measures, credentials, or access controls",
              ]}
            />
            <p>This obligation continues after your staff role ends.</p>
          </Section>

          <Section title="6. Conflicts of Interest">
            <p>
              If you have a personal relationship with an applicant or appellant
              that may affect your impartiality, declare this to senior staff
              and recuse yourself from the relevant decision.
            </p>
          </Section>

          <Section title="7. Security Responsibilities">
            <List
              items={[
                "Never share, leak, or expose credentials, tokens, API keys, or secrets related to Outfall systems",
                "Report any security issue to leadership immediately — do not publicise it",
                "Keep your Discord account secure and enable two-factor authentication",
                "Do not access Outfall admin systems from shared or public devices",
              ]}
            />
          </Section>

          <Section title="8. Termination of Access">
            <p>Staff access may be revoked at any time by leadership for:</p>
            <List
              items={[
                "Violation of this Agreement",
                "Violation of Outfall's community rules",
                "Inactivity beyond the period set by leadership",
                "Resignation",
                "Any conduct deemed harmful to Outfall or its community",
              ]}
            />
            <p>
              Upon termination you must cease all access to staff systems
              immediately and maintain all confidentiality obligations.
            </p>
          </Section>

          <Section title="9. Acknowledgement">
            <p>
              By holding a staff role at Outfall, you confirm that you have read
              and understood this Agreement in full, agree to comply with all
              obligations set out herein, and understand that violations may
              result in removal from staff and a ban from the community.
            </p>
          </Section>
        </div>

        <div className="mt-16 rounded-[20px] border border-[#ffd978]/20 bg-black/40 p-6 text-center">
          <p className="text-sm font-bold text-white/40">
            This document is for Outfall staff only. For questions, contact
            leadership through designated staff channels.
          </p>
        </div>
      </section>
    </main>
  );
}
